import { useEffect, useState, type FormEvent } from 'react';
import { collection, doc, orderBy, query } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocRow, useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import type { Assessment, CoverageAnswer, TaxonomyTag } from '@/types/domain';
import { strings } from '@/strings';

const saveAssessment = callable<Record<string, unknown>, { orgId: string; gaps: string[] }>('saveAssessment');

const ANSWERS: Array<{ value: CoverageAnswer; label: string }> = [
  { value: 'have_the_craft', label: strings.coverageHaveCraft },
  { value: 'tasks_covered_only', label: strings.coverageTasksOnly },
  { value: 'nothing', label: strings.coverageNothing },
  { value: 'not_needed', label: strings.coverageNotNeeded },
];

const GAP_ANSWERS: CoverageAnswer[] = ['tasks_covered_only', 'nothing'];
const MAX_PRIORITIES = 3;

/**
 * The instrument, as a screen. Two things here are not cosmetic:
 *
 * The coverage question is asked about DOMAINS only. Asking it per leaf
 * competency would take an hour and produce worse answers.
 *
 * The priority list is built from the coverage answers rather than offered up
 * front, so an organization cannot rank a domain it just said it has covered —
 * which is the contradiction the server refuses anyway.
 */
export function AssessmentPage() {
  const { claims } = useAuth();
  const orgId = String(claims.orgId || '');
  const { rows: taxonomy } = useDocs<TaxonomyTag>(query(collection(db, 'taxonomy'), orderBy('path')), []);
  const { row: existing } = useDocRow<Assessment>(
    orgId ? doc(db, `orgs/${orgId}/assessment/current`) : null, [orgId]
  );

  const domains = taxonomy.filter((t) => t.parentId === null);

  const [ambition, setAmbition] = useState('');
  const [staff, setStaff] = useState('');
  const [volunteers, setVolunteers] = useState('');
  const [budgetBand, setBudgetBand] = useState('');
  const [coverage, setCoverage] = useState<Record<string, CoverageAnswer>>({});
  const [priorities, setPriorities] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setAmbition(existing.stage?.ambition || '');
    setStaff(String(existing.stage?.staff ?? ''));
    setVolunteers(String(existing.stage?.volunteers ?? ''));
    setBudgetBand(existing.stage?.budgetBand || '');
    setCoverage(existing.coverage || {});
    setPriorities(existing.priorities || []);
  }, [existing]);

  // A domain that stops being a gap must stop being a priority, or the server
  // refuses a submission the screen said was fine.
  useEffect(() => {
    setPriorities((prev) => prev.filter((id) => GAP_ANSWERS.includes(coverage[id])));
  }, [coverage]);

  const eligible = domains.filter((d) => GAP_ANSWERS.includes(coverage[d.id]));
  const allAnswered = domains.length > 0 && domains.every((d) => coverage[d.id]);
  const ready = allAnswered && priorities.length > 0 && priorities.length <= MAX_PRIORITIES;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await saveAssessment({
        stage: {
          ambition: ambition.trim(),
          staff: Number(staff) || 0,
          volunteers: Number(volunteers) || 0,
          budgetBand,
        },
        coverage,
        priorities,
      });
      setMessage(strings.assessmentSaved);
    } catch (err) {
      setError(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex max-w-2xl flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-bold text-primary">{strings.assessmentTitle}</h1>
        <p className="text-sm text-muted">{strings.assessmentIntro}</p>
      </div>

      <fieldset className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
        <legend className="px-1 text-sm font-semibold text-ink">{strings.assessmentStage}</legend>
        <label className="flex flex-col gap-1 text-sm" htmlFor="staff">
          {strings.assessmentStaff}
          <input id="staff" type="number" min={0} value={staff} onChange={(e) => setStaff(e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor="volunteers">
          {strings.assessmentVolunteers}
          <input id="volunteers" type="number" min={0} value={volunteers} onChange={(e) => setVolunteers(e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor="budget">
          {strings.assessmentBudget}
          <input id="budget" value={budgetBand} onChange={(e) => setBudgetBand(e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor="ambition">
          {strings.assessmentAmbition}
          <textarea id="ambition" rows={2} value={ambition} onChange={(e) => setAmbition(e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>
      </fieldset>

      <fieldset className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
        <legend className="px-1 text-sm font-semibold text-ink">{strings.assessmentCoverage}</legend>
        <p className="text-xs text-muted">{strings.coverageHint}</p>
        {domains.map((domain) => (
          <label key={domain.id} className="flex items-center justify-between gap-3 text-sm" htmlFor={`cov-${domain.id}`}>
            {domain.nameAr}
            <select
              id={`cov-${domain.id}`}
              aria-label={domain.nameAr}
              value={coverage[domain.id] || ''}
              onChange={(e) => setCoverage((prev) => ({ ...prev, [domain.id]: e.target.value as CoverageAnswer }))}
              className="w-64 rounded-md border border-border-subtle px-2 py-1"
            >
              <option value="" />
              {ANSWERS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </label>
        ))}
      </fieldset>

      <fieldset className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-white p-4">
        <legend className="px-1 text-sm font-semibold text-ink">{strings.assessmentPriorities}</legend>
        {eligible.map((domain) => (
          <label key={domain.id} className="flex items-center gap-2 text-sm" htmlFor={`pri-${domain.id}`}>
            <input
              id={`pri-${domain.id}`}
              type="checkbox"
              aria-label={`${strings.assessmentPriorities}: ${domain.nameAr}`}
              checked={priorities.includes(domain.id)}
              disabled={!priorities.includes(domain.id) && priorities.length >= MAX_PRIORITIES}
              onChange={(e) => setPriorities((prev) =>
                e.target.checked ? [...prev, domain.id] : prev.filter((id) => id !== domain.id))}
            />
            {domain.nameAr}
          </label>
        ))}
      </fieldset>

      <div className="flex items-center gap-3">
        <button type="submit" disabled={!ready || busy}
          className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
          {strings.assessmentSave}
        </button>
        {message ? <span className="text-sm text-success">{message}</span> : null}
        {error ? <span role="alert" className="text-sm text-danger">{error}</span> : null}
      </div>
    </form>
  );
}
